import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject, SyntheticEvent } from 'react'
import { getAiCompletions } from '../../../../api/commonApi'
import { useUserStore } from '../../../stores/useUserStore'
import FORMAL_ICON from '@public/assets/formal.svg'
import LONG_ICON from '@public/assets/long.svg'
import SHORT_ICON from '@public/assets/short.svg'

export type NarrativeStyleKey = 'short' | 'long' | 'formal'

export type AutoNarrativeDraftingItem = Record<string, unknown> & {
  children?: unknown
  is_group_parent?: unknown
  matter_type?: unknown
}

export type AutoNarrativeDraftingOption = {
  key: NarrativeStyleKey
  icon: string
  query: string
}

export type AutoNarrativeDraftingState = {
  aiValue: string
  currentValue: string
  enabled: boolean
  isGenerating: boolean
  isOverlayVisible: boolean
  isPanelVisible: boolean
  openSetting: boolean
  resetDraftState: () => void
  settingAnchor: RefObject<HTMLImageElement>
  settingPopup: RefObject<HTMLDivElement>
  settingOptions: AutoNarrativeDraftingOption[]
  valueStyle: NarrativeStyleKey
  handleAccept: (event?: SyntheticEvent) => void
  handleDiscard: (event?: SyntheticEvent) => void
  handleGenerate: (
    event?: SyntheticEvent,
    nextStyle?: NarrativeStyleKey
  ) => Promise<void>
  handleSelectValueStyle: (option: AutoNarrativeDraftingOption) => void
  handleSettingClick: (event: SyntheticEvent) => void
}

type AiCompletionResult = {
  data?: unknown
}

type UseAutoNarrativeDraftingOptions = {
  enabled?: boolean
  field?: string
  item?: AutoNarrativeDraftingItem | null
  onAccept: (nextValue: string) => void
  onSuggestWithOcg?: (suggestedNarrative: string) => void
  onReset?: () => void
  useOcgSuggestion?: boolean
  value?: string | null
}

export const autoNarrativeDraftingOptions: AutoNarrativeDraftingOption[] = [
  {
    key: 'short',
    icon: SHORT_ICON,
    query: 'make it short length.',
  },
  {
    key: 'long',
    icon: LONG_ICON,
    query: 'make it long length. Not exceeding 50 words.',
  },
  {
    key: 'formal',
    icon: FORMAL_ICON,
    query: 'make it formal.',
  },
]

const isNarrativeStyleKey = (
  value: string | null
): value is NarrativeStyleKey =>
  value === 'short' || value === 'long' || value === 'formal'

const getInitialStyle = (): NarrativeStyleKey => {
  const storedValue = sessionStorage.getItem('valueStyle')
  return isNarrativeStyleKey(storedValue) ? storedValue : 'short'
}

const getTextValue = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

const buildDraftingQuery = ({
  field,
  item,
  styleKey,
  value,
}: {
  field: string
  item?: AutoNarrativeDraftingItem | null
  styleKey: NarrativeStyleKey
  value: string
}) => {
  const settingOption =
    autoNarrativeDraftingOptions.find(option => option.key === styleKey) ??
    autoNarrativeDraftingOptions[0]

  if (
    !value &&
    item?.is_group_parent === true &&
    Array.isArray(item.children)
  ) {
    const childNarratives = item.children
      .map(child =>
        child && typeof child === 'object'
          ? getTextValue((child as Record<string, unknown>)[field])
          : ''
      )
      .filter(Boolean)
      .join('; ')

    return `re-write:${childNarratives}`
  }

  const matterType = getTextValue(item?.matter_type)
  if (matterType) {
    return `generate billable time entry description for project type ${matterType} with these information ${value}; ${settingOption.query}`
  }

  return `generate billable time entry description with these information ${value}; ${settingOption.query}`
}

export const useAutoNarrativeDrafting = ({
  enabled = false,
  field = 'description',
  item,
  onAccept,
  onSuggestWithOcg,
  onReset,
  useOcgSuggestion = false,
  value,
}: UseAutoNarrativeDraftingOptions): AutoNarrativeDraftingState => {
  const selfUser = useUserStore(state => state.selfUser)
  const selectedUser = useUserStore(state => state.selectedUser)
  const isDelegationMode = useUserStore(state => state.isDelegationMode)
  const timekeeperId = (isDelegationMode ? selectedUser : selfUser)?.id
  const settingAnchor = useRef<HTMLImageElement>(null)
  const settingPopup = useRef<HTMLDivElement>(null)
  const [aiValue, setAiValue] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [openSetting, setOpenSetting] = useState(false)
  const [valueStyle, setValueStyle] =
    useState<NarrativeStyleKey>(getInitialStyle)

  const currentValue = value ?? ''

  const resetDraftState = useCallback(() => {
    setAiValue('')
    setIsGenerating(false)
    setOpenSetting(false)
  }, [])

  useEffect(() => {
    if (useOcgSuggestion) {
      setAiValue('')
    }
  }, [useOcgSuggestion])

  useEffect(() => {
    if (!openSetting) return

    const handleOutsideMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (
        settingAnchor.current?.contains(target) ||
        settingPopup.current?.contains(target)
      ) {
        return
      }

      setOpenSetting(false)
    }

    document.addEventListener('mousedown', handleOutsideMouseDown, true)

    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown, true)
    }
  }, [openSetting])

  const handleGenerate = useCallback(
    async (
      event?: SyntheticEvent,
      nextStyle: NarrativeStyleKey = valueStyle
    ) => {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      if (!enabled || isGenerating) return

      setOpenSetting(false)
      if (!useOcgSuggestion) {
        onReset?.()
      }
      setAiValue('')
      setIsGenerating(true)

      try {
        const query = buildDraftingQuery({
          field,
          item,
          styleKey: nextStyle,
          value: currentValue,
        })
        const aiResult = (await getAiCompletions({
          keyword: query,
          ...(timekeeperId ? { tkpr_id: timekeeperId } : {}),
        })) as AiCompletionResult
        const nextAiValue =
          typeof aiResult?.data === 'string'
            ? aiResult.data.replace(/"/g, '')
            : ''
        if (useOcgSuggestion) {
          onSuggestWithOcg?.(nextAiValue)
        } else {
          setAiValue(nextAiValue)
        }
      } catch (error) {
        console.error('Generate narrative failed:', error)
      } finally {
        setIsGenerating(false)
      }
    },
    [
      currentValue,
      enabled,
      field,
      isGenerating,
      item,
      onReset,
      onSuggestWithOcg,
      timekeeperId,
      useOcgSuggestion,
      valueStyle,
    ]
  )

  const handleAccept = useCallback(
    (event?: SyntheticEvent) => {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      if (!aiValue) return

      onAccept(aiValue)
      setAiValue('')
      onReset?.()
    },
    [aiValue, onAccept, onReset]
  )

  const handleDiscard = useCallback(
    (event?: SyntheticEvent) => {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      resetDraftState()
      onReset?.()
    },
    [onReset, resetDraftState]
  )

  const handleSettingClick = useCallback((event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpenSetting(current => !current)
  }, [])

  const handleSelectValueStyle = useCallback(
    (option: AutoNarrativeDraftingOption) => {
      sessionStorage.setItem('valueStyle', option.key)
      setValueStyle(option.key)
      setOpenSetting(false)
      if (currentValue) {
        void handleGenerate(undefined, option.key)
      }
    },
    [currentValue, handleGenerate]
  )

  return {
    aiValue,
    currentValue,
    enabled,
    isGenerating,
    isOverlayVisible: enabled && isGenerating && !currentValue,
    isPanelVisible:
      enabled &&
      !useOcgSuggestion &&
      Boolean(aiValue || (isGenerating && currentValue)),
    openSetting,
    resetDraftState,
    settingAnchor,
    settingPopup,
    settingOptions: autoNarrativeDraftingOptions,
    valueStyle,
    handleAccept,
    handleDiscard,
    handleGenerate,
    handleSelectValueStyle,
    handleSettingClick,
  }
}
